import React, { useState, useEffect, useCallback } from "react";
import "../style.scss";
import {
  ActivityLogIcon,
  AssigneeIcon,
  DatePickerIcon,
  EditIcon,
  PrimaryTicketIcon,
  RightArrowIcon,
  TicketMergedIcon,
} from "../../../icons";
import Button from "../../../components/button";
import TagComp from "../../../components/tag";
import { useAssignTicket } from "../../../hooks/useAssignTicket";
import { useMembers } from "../../../hooks/useMembers";
import { Dropdown, Menu, message, Spin } from "antd";
import ConfirmModal from "../../../components/ModalLayout/components/ConfirmModal";
import ScheduleDeliveryModal from "../../../components/ModalLayout/components/ScheduleTimeModal";
import { useScheduleDelivery } from "../../../hooks/useTicketDetails";
import { AssignIcon } from "../../../layout/Components/icons";

interface AssigneeSectionProps {
  onOpenRemarks: (comment?: string) => void;
  onOpenMergedTickets: () => void;
  ticketType: "None" | "Primary" | "Merged";
  onSwitchToPrimary: () => void;
  ticketData?: any;
  refetchTickets: () => void;
}

type Member = {
  pikitup_member_id?: number;
  member_first_name?: string;
  member_surname?: string;
  official_id?: string | number;
  [k: string]: any;
};

const normalizeMember = (m: any): Member =>
  m
    ? {
        pikitup_member_id: m.pikitup_member_id ?? m.id ?? m.member_id,
        member_first_name: m.member_first_name ?? m.first_name ?? m.name,
        member_surname: m.member_surname ?? m.surname ?? "",
        official_id: m.official_id ?? m.pikitup_member_id ?? m.id ?? "",
        ...m,
      }
    : ({} as Member);

const AssigneeSection: React.FC<AssigneeSectionProps> = ({
  onOpenRemarks,
  onOpenMergedTickets,
  ticketType,
  onSwitchToPrimary,
  ticketData,
  refetchTickets,
}) => {
  const [assignConfirmOpen, setAssignConfirmOpen] = useState(false);

  const [openDropdown, setOpenDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [assignedUser, setAssignedUser] = useState<Member | null>(null);
  const [assignSuccess, setAssignSuccess] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [confirmedSchedule, setConfirmedSchedule] = useState<{
    date: string;
    session: string;
  } | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [isAssigneeCleared, setIsAssigneeCleared] = useState(false);

  // mutation + members hook
  const { mutate: assignTicket, isPending: assigning } = useAssignTicket();
  const { data: membersDataRaw, isLoading: membersLoading } = useMembers(6, {}); // adjust args as needed

  // Normalize members into consistent shape
  const membersData: Member[] = (membersDataRaw ?? []).map((m: any) =>
    normalizeMember(m)
  );

  console.log(membersData, "membersData");

  // Set initial assignedUser from ticketData (if present)
  useEffect(() => {
    if (ticketData) {
      if (ticketData.assignee && typeof ticketData.assignee === "object") {
        setAssignedUser(normalizeMember(ticketData.assignee));
      } else if (ticketData.assignee_name || ticketData.assignee_id) {
        // fallback if only name/id provided
        setAssignedUser({
          member_first_name: ticketData.assignee_name,
          official_id: ticketData.assignee_id,
        } as Member);
      } else {
        setAssignedUser(null);
      }
    }
  }, [ticketData]);

  // debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim().toLowerCase());
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleScheduleConfirm = useCallback(
    async (data: { date: string; session: string }) => {
      try {
        setApiLoading(true);

        await useScheduleDelivery({
          pikitup_ticket_id: ticketData?.pikitup_ticket_id, // <-- already available
          scheduled_date: data.date,
          schedule_time_of_day: data.session,
        });
        refetchTickets(); // refresh UI

        setConfirmedSchedule(data);
        setScheduleModalOpen(false);

        message.success("Delivery scheduled successfully");
      } catch (err) {
        console.error(err);
        message.error("Failed to schedule delivery");
      } finally {
        setApiLoading(false);
      }
    },
    [ticketData]
  );

  const formatScheduleDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };
  // Filter members by first, surname or id
  const filteredMembers = membersData.filter((m) => {
    if (!debouncedSearch) return true;
    const first = (m.member_first_name ?? "").toString().toLowerCase();
    const last = (m.member_surname ?? "").toString().toLowerCase();
    const id = (m.official_id ?? m.pikitup_member_id ?? "")
      .toString()
      .toLowerCase();
    return (
      first.includes(debouncedSearch) ||
      last.includes(debouncedSearch) ||
      id.includes(debouncedSearch)
    );
  });

  // handle assign (works for both new assign and reassign)
  const handleAssign = (member: Member) => {
    // prevent double clicks while assigning
    if (!ticketData?.pikitup_ticket_id || assigning) return;

    // ensure member has id to send
    const assigneeId = Number(
      member.pikitup_member_id ?? member.official_id ?? member.id
    );
    if (!assigneeId) {
      console.warn("member has no id, cannot assign", member);
      return;
    }

    assignTicket(
      {
        pikitup_ticket_id: ticketData.pikitup_ticket_id,
        assignee_id: assigneeId,
      },
      {
        onSuccess: () => {
          // update UI
          setAssignedUser(member);
          setAssignSuccess(true);

          // show success briefly then hide success state (dropdown stays open unless user closes)
          setTimeout(() => setAssignSuccess(false), 1200);

          // optional: auto-close dropdown on success after short delay
          // setTimeout(() => setOpenDropdown(false), 900);
        },
        onError: (err: any) => {
          console.error("assign failed", err);
        },
      }
    );
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewImages, setPreviewImages] = useState<string[]>([]);

  const openPreview = (files: any[], index: number) => {
    setPreviewImages(files.map((f) => f.filepath));
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const closePreview = () => setPreviewOpen(false);

  const showAssigneeSection =
    ticketType !== "Merged" &&
    (assignedUser || ticketData?.assignee || ticketData?.assignee_name);

  const driverMembers = membersData.filter(
    (member) =>
      member.member_type === 6 ||
      member.member_type_name?.toLowerCase() === "driver"
  );

  const handleDriverSelect = useCallback(
    (driverId: string) => {
      const selectedDriver = driverMembers.find(
        (driver) => driver.pikitup_member_id === driverId
      );

      if (!selectedDriver || !ticketData?.pikitup_ticket_id) {
        message.error("Driver not found or ticket ID missing");
        return;
      }

      assignTicket(
        {
          pikitup_ticket_ids: [ticketData.pikitup_ticket_id],
          driver_id: Number(selectedDriver.pikitup_member_id),
        },
        {
          onSuccess: () => {
            setAssignedUser(normalizeMember(selectedDriver));
            message.success(
              `Assigned to driver: ${selectedDriver.member_first_name} ${selectedDriver.member_surname}`
            );
            refetchTickets();
          },
          onError: (err: any) => {
            console.error("Driver assignment failed", err);
            message.error("Failed to assign driver");
          },
        }
      );
    },
    [driverMembers, ticketData, assignTicket, refetchTickets]
  );

  const driverMenuItems = driverMembers.map((driver) => ({
    key: driver.pikitup_member_id,
    label: (
      <div className="driver-menu-item">
        <div className="driver-name">
          {driver.member_first_name} {driver.member_surname}
        </div>
        <div className="driver-id">
          {driver.official_id || driver.pikitup_member_id}
        </div>
        {driver.member_mobile_no && (
          <div className="driver-phone">{driver.member_mobile_no}</div>
        )}
      </div>
    ),
  }));

  const driverMenu = (
    <Menu
      items={driverMenuItems}
      onClick={({ key }) => handleDriverSelect(key)}
      style={{ maxHeight: "300px", overflowY: "auto" }}
      className="driver-dropdown-menu"
    />
  );

  const showAssignee =
    isAssigneeCleared ||
    ticketData?.status_flg === 36 ||
    ticketData?.status_flg === 22;

  console.log(ticketData, "ticketDataticketData");

  return (
    <div className="assignee-details-frame">
      {showAssigneeSection && (
        <div className="header-wrapper flex flex-col gap-4 relative">
          <span className="details-card-heading">Assignee</span>

          {/* display assigned user */}
          {assignedUser ? (
            <div className="assigned-user flex items-center justify-between gap-4">
              <div className="flex flex-col gap-2">
                <span className="assigned-user-name">
                  {assignedUser.member_first_name ?? "—"}
                </span>
                {assignedUser.official_id !== undefined && (
                  <span className="assigned-user-id">
                    {assignedUser.official_id}
                  </span>
                )}
              </div>
              {/* {isPermit(2, "edit") && (
                <Button
                  icon={<EditIcon />}
                  className="assigned-user-edit transparent"
                  onClick={() => {
                    // open dropdown to allow reassign
                    setOpenDropdown((s) => !s);
                    // clear previous search
                    setSearchTerm("");
                  }}
                />
              // )} */}
            </div>
          ) : (
            ticketData?.status_flg === 1 && (
              <Button
                icon={<AssigneeIcon />}
                iconPosition="start"
                className="flex items-center gap-3 transparent assignee-btn"
                onClick={() => {
                  // Only open modal for new/unassigned tickets
                  if (
                    ticketData?.status_flg === 10 || // new/unassigned status
                    !ticketData?.assignee_id
                  ) {
                    setAssignConfirmOpen(true);
                  } else {
                    // if already assigned, open dropdown for reassignment
                    setOpenDropdown((s) => !s);
                    setSearchTerm("");
                  }
                }}
              >
                <span className="assignee-text">Assign to group</span>
              </Button>
            )
          )}

          {/* assign / reassign dropdown */}

          {/* dropdown */}
          {openDropdown && (
            <div className="assignee-dropdown absolute mt-12 w-72 bg-white rounded-md shadow-lg p-3 z-50">
              {/* overlay sits at top so list is still visible underneath */}
              {(assigning || assignSuccess) && (
                <div className="assigning-overlay">
                  <div className="assigning-content">
                    {assigning ? (
                      <>
                        <Spin size="small" />
                        <span className="assigning-text">Assigning...</span>
                      </>
                    ) : (
                      <span className="assigning-success">✅ Assigned</span>
                    )}
                  </div>
                </div>
              )}

              <input
                type="text"
                placeholder="Search name or ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 border rounded-md mb-2 text-sm"
              />

              <div className="users-wrapper members-list relative flex flex-col max-h-60 overflow-y-auto">
                {membersLoading ? (
                  <div className="flex items-center justify-center py-3">
                    <Spin size="small" />
                    <span className="ml-2">Loading members...</span>
                  </div>
                ) : filteredMembers.length > 0 ? (
                  filteredMembers.map((m) => {
                    const isCurrent =
                      assignedUser &&
                      (m.pikitup_member_id === assignedUser.pikitup_member_id ||
                        String(m.official_id) ===
                          String(assignedUser.official_id));
                    return (
                      <div
                        key={
                          m.pikitup_member_id ??
                          m.official_id ??
                          JSON.stringify(m)
                        }
                        onClick={() => handleAssign(m)}
                        className={`users-btn flex items-center justify-between p-2 rounded-md text-sm cursor-pointer hover:bg-gray-100 ${
                          assigning ? "assigning-disabled" : ""
                        } ${isCurrent ? "bg-gray-50" : ""}`}
                        role="button"
                        aria-disabled={assigning}
                      >
                        <div>
                          <div className="user-name">
                            {m.member_first_name ?? "Unknown"}
                            {m.member_surname ? ` ${m.member_surname}` : ""}
                          </div>
                          <div className="user-id text-xs text-gray-400">
                            {m.official_id ?? m.pikitup_member_id ?? ""}
                          </div>
                        </div>

                        {/* show a small check for current assignee */}
                        {isCurrent && (
                          <div className="text-green-600 text-sm">Assigned</div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-gray-400 text-sm p-2">
                    No results found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {/* SERVICE TYPE SELF ASSIGNEE */}
      {/* {ticketData?.service_type === 1 &&
        ticketData?.status_flg === 1 && (
          <div className="schedule-delivery-section">
            <span className="details-card-heading">Assignee</span>

            <div className="assignee-section mt-4 mb-4">
              <Button
                icon={<AssigneeIcon />}
                iconPosition="start"
                className="flex items-center gap-3 transparent assignee-btn"
                onClick={() => {
                  assignTicket(
                    {
                      pikitup_ticket_ids: [
                        ticketData?.pikitup_ticket_id,
                      ],
                    },
                    {
                      onSuccess: () => {
                        setAssignConfirmOpen(false);
                        setAssignedUser({ member_first_name: "Myself" });
                        refetchTickets();
                      },
                    }
                  );
                }}
              >
                <span className="assignee-text">Assign to Myself</span>
              </Button>
            </div>
          </div>
        )} */}
      {/* ASSIGN DRIVER */}
      {/* CASE 1: USER CLEARED ASSIGNEE → SHOW ASSIGN BUTTON */}
      {isAssigneeCleared ? (
        <div className="assignee-section mt-4 mb-4 schedule-delivery-section">
          {/* <span className="details-card-heading mb-5">Assignee</span> */}

          <Dropdown
            overlay={driverMenu}
            trigger={["click"]}
            placement="bottomLeft"
            disabled={driverMembers.length === 0 || membersLoading}
            overlayClassName="driver-dropdown-overlay"
          >
            <Button
              icon={<AssigneeIcon />}
              iconPosition="start"
              className="flex items-center gap-3 transparent assignee-btn"
              loading={membersLoading}
            >
              <span className="assignee-text">
                {membersLoading
                  ? "Loading drivers..."
                  : `Assign to driver (${driverMembers.length} available)`}
              </span>
            </Button>
          </Dropdown>

          {!membersLoading && driverMembers.length === 0 && (
            <div className="mt-2 text-sm text-gray-500">
              No drivers available for assignment
            </div>
          )}
        </div>
      ) : ticketData?.status_flg === 22 ? (
        /* CASE 3: DEFAULT ASSIGN BUTTON */
        <div className="assignee-section mt-4 ">
          <span className="details-card-heading mb-4">Assignee</span>
          <div className="mt-4 mb-4">
            <Dropdown
              overlay={driverMenu}
              trigger={["click"]}
              placement="bottomLeft"
              disabled={driverMembers.length === 0 || membersLoading}
              overlayClassName="driver-dropdown-overlay"
            >
              <Button
                icon={<AssigneeIcon />}
                iconPosition="start"
                className="flex items-center gap-3 transparent assignee-btn"
                loading={membersLoading}
              >
                <span className="assignee-text">
                  {membersLoading
                    ? "Loading drivers..."
                    : `Assign to driver (${driverMembers.length} available)`}
                </span>
              </Button>
            </Dropdown>
          </div>

          {!membersLoading && driverMembers.length === 0 && (
            <div className="mt-2 text-sm text-gray-500">
              No drivers available for assignment
            </div>
          )}
        </div>
      ) : null}
      {ticketData?.status_flg === 35 && (
        <div className="schedule-delivery-section">
          <span className="details-card-heading">Schedule delivery time</span>

          {confirmedSchedule ? (
            <div className="confirmed-schedule flex">
              <div className="schedule-item">
                <span className="schedule-label">
                  {confirmedSchedule.session}
                </span>
                <span className="schedule-date">
                  {formatScheduleDate(confirmedSchedule.date)}
                </span>
              </div>

              <div className="schedule-actions">
                <button
                  className="edit-schedule-btn"
                  onClick={() => setScheduleModalOpen(true)}
                  title="Edit schedule"
                >
                  <EditIcon />
                </button>
              </div>
            </div>
          ) : (
            <div className="confirmed-schedule">
              <span
                className="datepicker-icon-wrapper"
                onClick={() => setScheduleModalOpen(true)}
              >
                <DatePickerIcon />
              </span>{" "}
              <span className="content">Choose a delivery time</span>
            </div>
          )}
        </div>
      )}
      {ticketData?.status_flg === 22 && (
        <div className="schedule-delivery-section">
          <span className="details-card-heading">Scheduled Delivery</span>

          <div className="confirmed-schedule flex opacity-70 cursor-not-allowed">
            <div className="schedule-item">
              <span className="schedule-label">
                {ticketData?.schedule_time_of_day}
              </span>
              <span className="schedule-date">
                {formatScheduleDate(ticketData?.scheduled_date)}
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="activity-log-frame">
        <span className="details-card-heading">Activity Log</span>
        <div className="activity-log-list flex flex-col gap-3 mt-3 max-h-96 overflow-y-auto">
          {ticketData?.activity_logs?.map((log: any, index: number) => {
            console.log("Activity Log:", log); // ← LOG HERE

            return (
              <div
                key={index}
                className="activity-card flex flex-col gap-2 p-3 rounded-md shadow-sm"
              >
                <div className="flex items-start gap-2">
                  <span className="activity-log-icon">
                    <ActivityLogIcon />
                  </span>

                  <div className="activity-content-wrapper flex flex-col">
                    <span className="activity-content text-sm">
                      {log.new_status}
                    </span>

                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span className="activity-log-time">
                        {log.created_at}
                      </span>
                    </div>

                    {log.comment && (
                      <Button
                        onClick={() => onOpenRemarks(log.comment)}
                        className="remarks-btn transparent"
                      >
                        Remarks
                      </Button>
                    )}

                    {log.files?.length > 0 && (
                      <div className="file-list">
                        {log.files.slice(0, 3).map((file: any, i: number) => (
                          <img
                            key={i}
                            src={file.filepath}
                            alt="attachment"
                            className="file-item"
                            onClick={() => openPreview(log.files, i)}
                          />
                        ))}

                        {log.files.length > 3 && (
                          <div
                            className="more-files"
                            onClick={() => openPreview(log.files, 0)}
                          >
                            +{log.files.length - 3}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {ticketData?.related_tickets?.length > 0 && (
        <div className="tickets-relationships-wrapper flex flex-col">
          <span className="details-card-heading">Relationships</span>

          {ticketType !== "Merged" && (
            <div className="button-wrapper">
              <Button
                onClick={onOpenMergedTickets}
                className="transparent merged-tickets-btn"
              >
                <TagComp
                  icon={<TicketMergedIcon />}
                  className="tag-merged-ticket"
                >
                  {`${ticketData?.related_tickets?.length} merged tickets`}
                </TagComp>
              </Button>
            </div>
          )}

          {ticketType === "Merged" && (
            <div onClick={onSwitchToPrimary} className="primary-ticket-card">
              <div className="card-header flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="ticket-id">#863765</span>
                  <TagComp className="tag-primary-ticket">
                    Primary ticket
                  </TagComp>
                </div>
                <span className="arrow-icon">
                  <RightArrowIcon />
                </span>
              </div>

              <div className="flex flex-col gap-3">
                <span className="ticket-title flex gap-2 items-center">
                  Animal Dead
                  <PrimaryTicketIcon />
                </span>
                <div className="flex flex-wrap gap-3">
                  <TagComp className="ant-tag-ticket">
                    Dead animal removal
                  </TagComp>
                  <TagComp className="ant-tag-ticket">Incident</TagComp>
                  <TagComp className="ant-tag-ticket">
                    12 Rosewood Drive, Sandton
                  </TagComp>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {previewOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <button
            onClick={closePreview}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              fontSize: "24px",
              color: "#fff",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
          <img
            src={previewImages[previewIndex]}
            alt="preview"
            style={{ maxHeight: "90%", maxWidth: "90%" }}
          />
        </div>
      )}
      {assignConfirmOpen && (
        <ConfirmModal
          open={assignConfirmOpen}
          title="Assign ticket"
          message={`You are about to assign this ticket to yourself.`}
          confirmText="Assign"
          loading={assigning}
          onClose={() => setAssignConfirmOpen(false)}
          onConfirm={() => {
            if (!ticketData?.pikitup_ticket_id) return;

            assignTicket(
              {
                pikitup_ticket_ids: [ticketData.pikitup_ticket_id],
              },
              {
                onSuccess: () => {
                  setAssignConfirmOpen(false);
                  setAssignedUser({
                    member_first_name: "Myself",
                  });
                  refetchTickets();
                },
                onError: (err: any) => {
                  console.error("Assign failed", err);
                },
              }
            );
          }}
        />
      )}{" "}
      <ScheduleDeliveryModal
        loading={apiLoading}
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        onConfirm={handleScheduleConfirm}
        sessionOptions={[
          "Morning session",
          "Afternoon session",
          "Evening session",
        ]}
      />
    </div>
  );
};

export default AssigneeSection;
